import type { Guide } from './guides';
import type { Point, Rect } from './types';

export interface GridSettings {
  /** Spacing of the major grid lines, in document pixels. */
  size: number;
  /** How many cells each major cell is divided into. */
  subdivisions: number;
}

export interface SnapSettings {
  enabled: boolean;
  toGuides: boolean;
  toGrid: boolean;
  /** The canvas edges and its centre lines. */
  toDocument: boolean;
}

/** Lines a drag can latch onto, in document pixels, split by axis. */
export interface SnapTargets {
  xs: number[];
  ys: number[];
}

export const EMPTY_TARGETS: SnapTargets = { xs: [], ys: [] };

export function snapTargets(
  size: { width: number; height: number },
  guides: readonly Guide[],
  grid: GridSettings,
  settings: SnapSettings,
): SnapTargets {
  if (!settings.enabled) return EMPTY_TARGETS;
  const xs: number[] = [];
  const ys: number[] = [];

  if (settings.toGuides) {
    for (const guide of guides) {
      (guide.axis === 'horizontal' ? ys : xs).push(guide.position);
    }
  }
  if (settings.toDocument) {
    xs.push(0, size.width / 2, size.width);
    ys.push(0, size.height / 2, size.height);
  }
  if (settings.toGrid) {
    const step = gridStep(grid);
    if (step > 0) {
      for (let x = 0; x <= size.width; x += step) xs.push(x);
      for (let y = 0; y <= size.height; y += step) ys.push(y);
    }
  }
  return { xs, ys };
}

/** Spacing of the finest grid line, which is what snapping uses. */
export function gridStep(grid: GridSettings): number {
  const divisions = Math.max(1, Math.round(grid.subdivisions));
  return grid.size > 0 ? grid.size / divisions : 0;
}

function nearest(value: number, candidates: readonly number[], tolerance: number): number | null {
  let best: number | null = null;
  let bestDistance = tolerance;
  for (const candidate of candidates) {
    const away = Math.abs(candidate - value);
    if (away <= bestDistance) {
      best = candidate;
      bestDistance = away;
    }
  }
  return best;
}

/** Pulls a point onto the nearest line on each axis independently. */
export function snapPoint(point: Point, targets: SnapTargets, tolerance: number): Point {
  return {
    x: nearest(point.x, targets.xs, tolerance) ?? point.x,
    y: nearest(point.y, targets.ys, tolerance) ?? point.y,
  };
}

/**
 * Offset that puts a moving box onto the nearest line. Both edges and the centre
 * are candidates, and the closest of the three wins, which is what makes
 * dragging a layer feel like it clicks into place.
 */
export function snapRectDelta(rect: Rect, targets: SnapTargets, tolerance: number): Point {
  const axis = (start: number, extent: number, candidates: readonly number[]): number => {
    let best = 0;
    let bestDistance = tolerance;
    for (const edge of [start, start + extent / 2, start + extent]) {
      for (const candidate of candidates) {
        const away = Math.abs(candidate - edge);
        if (away < bestDistance) {
          best = candidate - edge;
          bestDistance = away;
        }
      }
    }
    return best;
  };

  return {
    x: axis(rect.x, rect.width, targets.xs),
    y: axis(rect.y, rect.height, targets.ys),
  };
}
